import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Home,
  Shield,
  Loader2,
  FileText,
  Download,
  ChevronRight,
  Target,
  ListChecks,
  BarChart3,
  Clock,
  Lock,
  Sparkles,
  Award,
  ArrowRight,
  TrendingUp,
  Users,
  Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import analytics from "@/utils/analytics";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const outcomeConfig = {
  laag_risico: {
    title: "Laag risico",
    colorClass: "text-emerald-600",
    bgClass: "bg-emerald-50",
    borderClass: "border-emerald-200",
    darkBgClass: "bg-emerald-500/10",
    darkBorderClass: "border-emerald-500/20",
    darkTextClass: "text-emerald-400",
    icon: CheckCircle2
  },
  twijfelgeval: {
    title: "Twijfelgeval",
    colorClass: "text-amber-600",
    bgClass: "bg-amber-50",
    borderClass: "border-amber-200",
    darkBgClass: "bg-amber-500/10",
    darkBorderClass: "border-amber-500/20",
    darkTextClass: "text-amber-400",
    icon: AlertTriangle
  },
  verhoogd_risico: {
    title: "Verhoogd risico",
    colorClass: "text-red-600",
    bgClass: "bg-red-50",
    borderClass: "border-red-200",
    darkBgClass: "bg-red-500/10",
    darkBorderClass: "border-red-500/20",
    darkTextClass: "text-red-400",
    icon: XCircle
  }
};

const PremiumReport = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isPremiumLocked, setIsPremiumLocked] = useState(false);
  const hasTrackedView = useRef(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await axios.get(`${API_URL}/report/${submissionId}`);
        setReportData(response.data);
        
        // Track premium report view (only once)
        if (!hasTrackedView.current) {
          analytics.premiumReportViewed(submissionId);
          hasTrackedView.current = true;
        }
      } catch (err) {
        console.error("Error fetching report:", err);
        if (err.response?.status === 403) {
          setIsPremiumLocked(true);
          // Auto-redirect after 3 seconds
          setTimeout(() => {
            navigate(`/dba-check/resultaat/${submissionId}`);
          }, 3000);
        } else if (err.response?.status === 404) {
          setError("Rapport niet gevonden");
        } else {
          setError("Kon het rapport niet laden");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [submissionId, navigate]);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/report/${submissionId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `proofix_dba_rapport.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download failed:', error);
      alert('PDF download mislukt. Probeer het opnieuw.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
          </div>
          <p className="text-white font-medium text-lg mb-2">Uw premium rapport wordt geladen...</p>
          <p className="text-zinc-500 text-sm">Even geduld alstublieft</p>
        </motion.div>
      </div>
    );
  }

  // Premium locked - redirect to result page
  if (isPremiumLocked) {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-amber-400" />
          </div>
          <h1 className="font-outfit text-2xl font-medium text-white mb-3">
            Premium rapport niet ontgrendeld
          </h1>
          <p className="text-zinc-400 mb-6">
            U heeft nog geen toegang tot dit rapport. Ontgrendel het premium rapport om uw persoonlijke analyse te bekijken.
          </p>
          <p className="text-zinc-500 text-sm mb-6">
            U wordt automatisch doorgestuurd naar uw resultaat...
          </p>
          <Button
            onClick={() => navigate(`/dba-check/resultaat/${submissionId}`)}
            className="rounded-full px-8 py-6 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white"
            data-testid="unlock-btn"
          >
            Rapport ontgrendelen
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="font-outfit text-2xl font-medium text-white mb-3">
            {error}
          </h1>
          <p className="text-zinc-400 mb-6">
            Er ging iets mis bij het laden van uw rapport.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              className="rounded-full px-6 py-5 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Opnieuw proberen
            </Button>
            <Link to="/dba-check">
              <Button className="rounded-full px-6 py-5 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white">
                Nieuwe check starten
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  const { report, name, score, outcome, generated_at, is_ai_generated } = reportData;
  const outcomeStyle = outcomeConfig[outcome];
  const OutcomeIcon = outcomeStyle.icon;
  const generatedDate = new Date(generated_at).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-zinc-900/95 backdrop-blur border-b border-zinc-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link 
            to="/" 
            className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 text-sm"
            data-testid="home-link"
          >
            <Home className="w-4 h-4" />
            Proofix
          </Link>
          <Button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            variant="outline"
            className="rounded-full px-4 py-2 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white disabled:opacity-50"
            data-testid="download-pdf-btn"
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Downloaden...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </>
            )}
          </Button>
        </div>
      </header>

      {/* Report Content */}
      <main className="px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Premium Badge & Report Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            {/* Premium Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 text-blue-400 text-sm font-semibold uppercase tracking-wider mb-6">
              <Award className="w-4 h-4" />
              Premium DBA Rapport
              <Sparkles className="w-4 h-4" />
            </div>
            
            {/* Title with name */}
            <h1 className="font-outfit text-3xl md:text-4xl font-medium text-white mb-4">
              Persoonlijk Rapport voor <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1DA1F2] to-[#2563EB]">{name}</span>
            </h1>
            
            {/* Score and outcome badge */}
            <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${outcomeStyle.darkBgClass} ${outcomeStyle.darkBorderClass} border`}>
                <OutcomeIcon className={`w-4 h-4 ${outcomeStyle.darkTextClass}`} />
                <span className={`text-sm font-medium ${outcomeStyle.darkTextClass}`}>
                  {outcomeStyle.title}
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800 border border-zinc-700">
                <BarChart3 className="w-4 h-4 text-zinc-400" />
                <span className="text-sm font-medium text-zinc-300">Score: {score}/100</span>
              </div>
            </div>
            
            {/* Generated date and AI badge */}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <p className="text-zinc-500 text-sm">
                Gegenereerd op {generatedDate}
              </p>
              {is_ai_generated && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-400 text-xs font-medium">
                  <Sparkles className="w-3 h-3" />
                  AI Gepersonaliseerd
                </span>
              )}
            </div>
          </motion.div>

          {/* Section 1: Samenvatting */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-zinc-800/80 to-zinc-800/40 border border-zinc-700 rounded-2xl p-8 mb-6 relative overflow-hidden"
            data-testid="section-samenvatting"
          >
            {/* Decorative gradient */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl" />
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center border border-blue-500/20">
                  <Target className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-medium text-white">1. Samenvatting</h2>
                  <p className="text-zinc-500 text-sm">Overzicht van uw situatie</p>
                </div>
              </div>
              
              <h3 className="text-lg font-medium text-white mb-3">{report.samenvatting.titel}</h3>
              <p className="text-zinc-300 leading-relaxed mb-6">{report.samenvatting.tekst}</p>
              
              <div className={`p-5 rounded-xl ${outcomeStyle.darkBgClass} ${outcomeStyle.darkBorderClass} border`}>
                <div className="flex items-start gap-3">
                  <Shield className={`w-5 h-5 ${outcomeStyle.darkTextClass} flex-shrink-0 mt-0.5`} />
                  <p className={`text-sm font-medium ${outcomeStyle.darkTextClass}`}>
                    {report.samenvatting.conclusie}
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 2: Risicosignalen */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-8 mb-6"
            data-testid="section-risicosignalen"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center border border-amber-500/20">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h2 className="font-outfit text-xl font-medium text-white">2. Belangrijkste Risicosignalen</h2>
                <p className="text-zinc-500 text-sm">Punten die aandacht vereisen</p>
              </div>
            </div>
            
            {report.risicosignalen.length === 0 ? (
              <div className="flex items-center gap-4 p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-emerald-400 font-medium">Geen significante risicosignalen</p>
                  <p className="text-emerald-400/70 text-sm">Uw situatie vertoont geen belangrijke risicofactoren.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {report.risicosignalen.map((signal, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + index * 0.05 }}
                    className={`p-5 rounded-xl border ${
                      signal.niveau === "hoog" 
                        ? "bg-red-500/10 border-red-500/20" 
                        : "bg-amber-500/10 border-amber-500/20"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        signal.niveau === "hoog" ? "bg-red-500/20" : "bg-amber-500/20"
                      }`}>
                        {signal.niveau === "hoog" ? (
                          <XCircle className="w-5 h-5 text-red-400" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-amber-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs uppercase tracking-wider font-bold ${
                            signal.niveau === "hoog" ? "text-red-400" : "text-amber-400"
                          }`}>
                            {signal.niveau} risico
                          </span>
                          <span className="text-zinc-600">•</span>
                          <span className="text-zinc-500 text-xs capitalize">{signal.categorie}</span>
                        </div>
                        <p className="text-white font-medium mb-1">{signal.signaal}</p>
                        <p className="text-zinc-400 text-sm">{signal.toelichting}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.section>

          {/* Section 3: Analyse per onderdeel */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-8 mb-6"
            data-testid="section-analyse"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center border border-purple-500/20">
                <BarChart3 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="font-outfit text-xl font-medium text-white">3. Analyse per Onderdeel</h2>
                <p className="text-zinc-500 text-sm">Gedetailleerde beoordeling van uw antwoorden</p>
              </div>
            </div>
            
            <div className="space-y-8">
              {report.analyse_per_onderdeel.map((category, catIndex) => (
                <div key={catIndex} className="border-b border-zinc-700/50 pb-6 last:border-0 last:pb-0">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2 capitalize">
                    <ChevronRight className="w-5 h-5 text-blue-400" />
                    {category.categorie}
                  </h3>
                  <div className="space-y-3">
                    {category.onderdelen.map((item, itemIndex) => (
                      <motion.div 
                        key={itemIndex}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 + itemIndex * 0.03 }}
                        className="bg-zinc-800 rounded-xl p-5 hover:bg-zinc-800/80 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-zinc-400 text-sm mb-2">{item.vraag}</p>
                            <p className="text-white font-medium">{item.antwoord}</p>
                          </div>
                          <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${
                            item.beoordeling === "gunstig" 
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/20"
                              : item.beoordeling === "neutraal"
                              ? "bg-amber-500/20 text-amber-400 border border-amber-500/20"
                              : "bg-red-500/20 text-red-400 border border-red-500/20"
                          }`}>
                            {item.beoordeling === "gunstig" ? "✓ Gunstig" : 
                             item.beoordeling === "neutraal" ? "• Neutraal" : "✗ Ongunstig"}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Section 4: Verbeterpunten */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-8 mb-6"
            data-testid="section-verbeterpunten"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center border border-emerald-500/20">
                <ListChecks className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h2 className="font-outfit text-xl font-medium text-white">4. Concrete Verbeterpunten</h2>
                <p className="text-zinc-500 text-sm">Acties die u kunt ondernemen</p>
              </div>
            </div>
            
            <div className="space-y-6">
              {report.verbeterpunten.map((punt, index) => (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="bg-zinc-800 rounded-xl p-6 border border-zinc-700/50"
                >
                  <h3 className="text-white font-medium mb-4 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500/30 to-blue-600/30 text-blue-400 text-sm font-bold flex items-center justify-center border border-blue-500/20">
                      {index + 1}
                    </span>
                    {punt.titel}
                  </h3>
                  <ul className="space-y-3 ml-11">
                    {punt.acties.map((actie, actieIndex) => (
                      <li key={actieIndex} className="flex items-start gap-3 text-zinc-300">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span>{actie}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Section 5: Actieplan */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-br from-blue-900/40 via-blue-800/30 to-purple-900/20 border border-blue-700/40 rounded-2xl p-8 mb-6 relative overflow-hidden"
            data-testid="section-actieplan"
          >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl" />
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center border border-blue-500/20">
                  <Clock className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-medium text-white">5. Praktisch Actieplan</h2>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-blue-400">Prioriteit: {report.actieplan.prioriteit}</span>
                    <span className="text-zinc-600">•</span>
                    <span className="text-blue-300">{report.actieplan.termijn}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                {report.actieplan.stappen.map((stap, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="flex items-start gap-4 bg-zinc-800/60 rounded-xl p-5 border border-zinc-700/50"
                  >
                    <div className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500/20 to-blue-600/20 border border-blue-500/20">
                      <span className="text-blue-400 text-sm font-bold whitespace-nowrap">Week {stap.week}</span>
                    </div>
                    <p className="text-white flex-1 pt-1">{stap.actie}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center py-10"
          >
            {/* Disclaimer */}
            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-6 mb-8">
              <p className="text-zinc-500 text-sm leading-relaxed">
                <strong className="text-zinc-400">Disclaimer:</strong> Dit rapport is gegenereerd op basis van uw antwoorden en biedt indicatief advies. 
                De analyse vervangt geen professioneel juridisch of fiscaal advies. Voor een definitieve beoordeling adviseren wij contact met een specialist.
              </p>
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/dba-check/resultaat/${submissionId}`}>
                <Button 
                  variant="outline"
                  className="rounded-full px-6 py-5 border-zinc-700 text-zinc-300 hover:bg-zinc-800 w-full sm:w-auto"
                  data-testid="back-to-result-btn"
                >
                  Terug naar resultaat
                </Button>
              </Link>
              <Link to="/dba-check">
                <Button 
                  className="rounded-full px-6 py-5 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white w-full sm:w-auto"
                  data-testid="new-check-btn"
                >
                  Nieuwe check starten
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            {/* Trust badge */}
            <div className="mt-8 flex items-center justify-center gap-2 text-zinc-500 text-sm">
              <Shield className="w-4 h-4" />
              <span>Rapport gegenereerd door Proofix</span>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default PremiumReport;
